import React, { useEffect, useRef, useState } from 'react';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { relabel } from '../../../../stays/wording';
import { AlertPreview, previewAlert, sendAlertTest } from '../../../../api/stays-alerts-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, Label, Select } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import toast from '../../../../utils/toast';
import {
  ALERT_TEMPLATE_KINDS,
  ALERT_TEMPLATE_MAX,
  AlertTemplateKind,
  checkAlertTemplate,
  insertPlaceholder,
} from '../../../../utils/stays-template-placeholders';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { Card, CardTitle, Note, Preview, PreviewTitle, TruncNote } from './alerts.styled';

/**
 * THE MESSAGE STUDIO (plan S8, SC-1; R-S8.11). The host rewrites the four
 * templated alerts. The draft lives in the parent form (saved with "Save alert
 * settings"); `null` is "the default". The preview is the SERVER's render
 * (route 3, debounced 400 ms) with a real stay's first name when the listing
 * has one, else a sample. Nothing here writes.
 */

const BELL_LIMIT = 500;
const DEBOUNCE_MS = 400;

const PLACEHOLDER_WORDS: Record<string, string> = {
  listingName: 'Listing name',
  unitLabel: 'Unit',
  buildingName: 'Building',
  guestFirstName: 'Guest first name',
  partySize: 'Number of guests',
  source: 'Booking source',
  checkInDate: 'Check-in date',
  checkInTime: 'Check-in time',
  checkOutDate: 'Checkout date',
  checkOutTime: 'Checkout time',
  checkInStatus: 'Check-in status',
  cleaningStatus: 'Cleaning status',
  cleaningFee: 'Cleaning fee',
  nextGuestFirstName: 'Next guest first name',
  nextArrivalTime: 'Next arrival time',
  cleaningWindow: 'Cleaning window',
  cleaningWindowHours: 'Cleaning window (hours)',
};

export interface StudioProps {
  listingId: string;
  canManage: boolean;
  labels: Record<AlertTemplateKind, string>;
  descriptions: Record<AlertTemplateKind, string>;
  placeholders: Record<AlertTemplateKind, string[]>;
  defaults: Record<AlertTemplateKind, string>;
  /** The draft per kind: null = the default wording. */
  drafts: Record<AlertTemplateKind, string | null>;
  onDraft: (kind: AlertTemplateKind, text: string | null) => void;
  serverError: { kind: AlertTemplateKind; message: string } | null;
}

export const TemplateStudio: React.FC<StudioProps> = ({
  listingId,
  canManage,
  labels,
  descriptions,
  placeholders,
  defaults,
  drafts,
  onDraft,
  serverError,
}) => {
  const [kind, setKind] = useState<AlertTemplateKind>('guest_arrived');
  const [preview, setPreview] = useState<AlertPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const desk = useDeskInPlan();
  const [testing, setTesting] = useState(false);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const seq = useRef(0);

  const text = drafts[kind] == null ? defaults[kind] : (drafts[kind] as string);
  const local = checkAlertTemplate(kind, text);
  const invalid = !!local;
  const localWords = local
    ? local.code === 'TEMPLATE_PLACEHOLDER_UNKNOWN'
      ? `${local.placeholders.map(p => `{${p}}`).join(', ')} is not a detail this alert can use.`
      : local.code === 'TEMPLATE_EMPTY'
        ? 'Write the message before saving.'
        : local.code === 'TEMPLATE_TOO_LONG'
          ? `A message can be up to ${ALERT_TEMPLATE_MAX} characters.`
          : 'The message has characters that cannot be sent, or more than 30 lines.'
    : null;
  // The server's answer wins once it has one for this alert: it names the fix ("Use {guestFirstName}. …").
  // The page clears it on the next edit, so the local check speaks again while typing.
  const fieldError = (serverError && serverError.kind === kind ? serverError.message : null) || localWords;

  // The server's preview, 400 ms after the last keystroke; a stale answer is dropped.
  useEffect(() => {
    if (invalid) return undefined;
    const mine = ++seq.current;
    const id = window.setTimeout(async () => {
      try {
        const p = await previewAlert(listingId, kind, drafts[kind]);
        if (mine === seq.current) {
          setPreview(p);
          setPreviewError(null);
        }
      } catch (e) {
        if (mine === seq.current) setPreviewError(refusalText(e as StaysApiError, 'Could not show the preview.'));
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(id);
  }, [listingId, kind, drafts, invalid]);

  const insert = (name: string) => {
    const el = area.current;
    const out = insertPlaceholder(text, name, el ? el.selectionStart : null, el ? el.selectionEnd : null);
    onDraft(kind, out.text);
    window.requestAnimationFrame(() => {
      if (area.current) {
        area.current.focus();
        area.current.setSelectionRange(out.caret, out.caret);
      }
    });
  };

  const test = async () => {
    if (testing || local) return;
    setTesting(true);
    try {
      await sendAlertTest(listingId, kind, drafts[kind]);
      toast.success('Test sent. Check your notifications.');
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, 'The test could not be sent. Please try again.'));
    } finally {
      setTesting(false);
    }
  };

  const truncated = !!preview && preview.body_length > BELL_LIMIT;

  return (
    <Card aria-labelledby="alert-studio-title" data-testid="alert-studio">
      <CardTitle id="alert-studio-title">Message studio</CardTitle>
      <Field>
        <Label htmlFor={`studio-kind-${listingId}`}>Alert to edit</Label>
        <Select
          id={`studio-kind-${listingId}`}
          value={kind}
          onChange={e => {
            setKind(e.target.value as AlertTemplateKind);
            setPreview(null);
          }}
          aria-describedby={`studio-kind-note-${listingId}`}
        >
          {ALERT_TEMPLATE_KINDS.map(k => (
            <option key={k} value={k}>
              {labels[k]}
            </option>
          ))}
        </Select>
        <Note id={`studio-kind-note-${listingId}`}>{descriptions[kind]}</Note>
      </Field>
      <Field>
        <CountedTextarea
          ref={area}
          id={`studio-text-${listingId}`}
          label="Message"
          value={text}
          max={ALERT_TEMPLATE_MAX}
          rows={6}
          readOnly={!canManage}
          hasError={!!fieldError}
          onChange={e => onDraft(kind, e.target.value === defaults[kind] ? null : e.target.value)}
        />
        <FieldError message={fieldError || undefined} />
      </Field>
      {canManage ? (
        <ChipGroup aria-label="Insert a detail">
          {(placeholders[kind] || []).map(p => (
            <ToggleChip key={p} pressed={false} onToggle={() => insert(p)} aria-label={`Insert ${relabel(PLACEHOLDER_WORDS[p] || p, desk)}`}>
              {relabel(PLACEHOLDER_WORDS[p] || p, desk)}
            </ToggleChip>
          ))}
        </ChipGroup>
      ) : null}
      <div>
        <Note id={`studio-preview-label-${listingId}`}>Preview{preview && preview.sample ? ' (sample guests)' : ''}</Note>
        <Preview role="status" aria-labelledby={`studio-preview-label-${listingId}`} data-testid="alert-preview">
          {previewError ? previewError : preview ? (
            <>
              <PreviewTitle>{preview.title}</PreviewTitle>
              {preview.body}
            </>
          ) : (
            '…'
          )}
        </Preview>
        <TruncNote $warn={truncated}>The bell and lock screen show the first 500 characters</TruncNote>
      </div>
      {canManage ? (
        <>
          <AppButton type="button" variant="secondary" onClick={() => void test()} loading={testing} loadingLabel="Sending"
            disabled={!!local}>
            Send a test to me
          </AppButton>
          <AppButton type="button" variant="ghost" onClick={() => setConfirmReset(true)} disabled={drafts[kind] == null}>
            Reset to default
          </AppButton>
        </>
      ) : null}
      <ConfirmDialog
        open={confirmReset}
        title={`Reset the ${labels[kind]} message?`}
        message="The message goes back to Lobbero's wording when you save."
        primaryLabel="Reset"
        onPrimary={() => {
          onDraft(kind, null);
          setConfirmReset(false);
        }}
        dismissLabel="Keep my wording"
        onDismiss={() => setConfirmReset(false)}
      />
    </Card>
  );
};
